import { DateTime } from 'luxon'
import { CountryId } from '@/network/graphql.g'

const WORKING_DAYS_IN_WEEK = 5
const SATURDAY = 6
const SUNDAY = 7

type HolidayRules = {
  fixed: string[]
  easter?: { orthodox?: boolean; offsets: number[] }
  weekendSubstitution?: { weekdays: number[]; except?: string[] }
  extraDaysOff?: string[]
  cancelledDaysOff?: string[]
}

const HOLIDAY_RULES: Partial<Record<CountryId, HolidayRules>> = {
  [CountryId.Sk]: {
    fixed: [
      '01-01',
      '01-06',
      '05-01',
      '05-08',
      '07-05',
      '08-29',
      '09-15',
      '11-01',
      '12-24',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [-2, 1] },
    cancelledDaysOff: ['2026-05-08', '2026-09-15']
  },
  [CountryId.Cz]: {
    fixed: [
      '01-01',
      '05-01',
      '05-08',
      '07-05',
      '07-06',
      '09-28',
      '10-28',
      '11-17',
      '12-24',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [-2, 1] }
  },
  [CountryId.Pl]: {
    fixed: [
      '01-01',
      '01-06',
      '05-01',
      '05-03',
      '08-15',
      '11-01',
      '11-11',
      '12-24',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [1, 60] }
  },
  [CountryId.Hr]: {
    fixed: [
      '01-01',
      '01-06',
      '05-01',
      '05-30',
      '06-22',
      '08-05',
      '08-15',
      '11-01',
      '11-18',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [1, 60] }
  },
  [CountryId.Hu]: {
    fixed: [
      '01-01',
      '03-15',
      '05-01',
      '08-20',
      '10-23',
      '11-01',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [-2, 1, 50] },
    extraDaysOff: ['2026-01-02', '2026-08-21', '2026-12-24']
  },
  [CountryId.Si]: {
    fixed: [
      '01-01',
      '01-02',
      '02-08',
      '04-27',
      '05-01',
      '05-02',
      '06-25',
      '08-15',
      '10-31',
      '11-01',
      '12-25',
      '12-26'
    ],
    easter: { offsets: [1] }
  },
  [CountryId.Bg]: {
    fixed: [
      '01-01',
      '03-03',
      '05-01',
      '05-06',
      '05-24',
      '09-06',
      '09-22',
      '12-24',
      '12-25',
      '12-26'
    ],
    easter: { orthodox: true, offsets: [-2, 1] },
    weekendSubstitution: { weekdays: [SATURDAY, SUNDAY] },
    extraDaysOff: ['2026-01-02']
  },
  [CountryId.Rs]: {
    fixed: [
      '01-01',
      '01-02',
      '01-07',
      '02-15',
      '02-16',
      '05-01',
      '05-02',
      '11-11'
    ],
    easter: { orthodox: true, offsets: [-2, 1] },
    weekendSubstitution: { weekdays: [SUNDAY], except: ['01-07'] }
  },
  [CountryId.Ba]: {
    fixed: ['01-01', '01-02', '05-01', '05-02'],
    weekendSubstitution: { weekdays: [SUNDAY] }
  },
  [CountryId.Gr]: {
    fixed: [
      '01-01',
      '01-06',
      '03-25',
      '05-01',
      '08-15',
      '10-28',
      '12-25',
      '12-26'
    ],
    easter: { orthodox: true, offsets: [-48, -2, 1, 50] }
  },
  [CountryId.Ro]: {
    fixed: [
      '01-01',
      '01-02',
      '01-06',
      '01-07',
      '01-24',
      '05-01',
      '06-01',
      '08-15',
      '11-30',
      '12-01',
      '12-25',
      '12-26'
    ],
    easter: { orthodox: true, offsets: [-2, 1, 50] }
  }
}

const westernEaster = (year: number): DateTime => {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1

  return DateTime.utc(year, month, day)
}

// Julian calendar Easter moved by the 13 days between the calendars (valid 1900-2099)
const orthodoxEaster = (year: number): DateTime => {
  const a = year % 4
  const b = year % 7
  const c = year % 19
  const d = (19 * c + 15) % 30
  const e = (2 * a + 4 * b - d + 34) % 7
  const month = Math.floor((d + e + 114) / 31)
  const day = ((d + e + 114) % 31) + 1

  return DateTime.utc(year, month, day).plus({ days: 13 })
}

const buildHolidays = (year: number, rules: HolidayRules): Set<string> => {
  const holidays = new Set(rules.fixed.map((monthDay) => `${year}-${monthDay}`))

  if (rules.easter) {
    const easter = rules.easter.orthodox
      ? orthodoxEaster(year)
      : westernEaster(year)
    rules.easter.offsets.forEach((offset) =>
      holidays.add(easter.plus({ days: offset }).toISODate())
    )
  }

  rules.extraDaysOff
    ?.filter((date) => date.startsWith(`${year}-`))
    .forEach((date) => holidays.add(date))

  if (rules.weekendSubstitution) {
    const { weekdays, except = [] } = rules.weekendSubstitution
    ;[...rules.fixed]
      .sort()
      .filter((monthDay) => !except.includes(monthDay))
      .map((monthDay) =>
        DateTime.fromISO(`${year}-${monthDay}`, { zone: 'utc' })
      )
      .filter((holiday) => weekdays.includes(holiday.weekday))
      .forEach((holiday) => {
        let substitute = holiday.plus({ days: 1 })
        while (
          substitute.weekday > WORKING_DAYS_IN_WEEK ||
          holidays.has(substitute.toISODate())
        ) {
          substitute = substitute.plus({ days: 1 })
        }
        holidays.add(substitute.toISODate())
      })
  }

  rules.cancelledDaysOff?.forEach((date) => holidays.delete(date))

  return holidays
}

const holidaysCache = new Map<string, Set<string>>()

const getHolidays = (year: number, country?: string): Set<string> => {
  const rules = HOLIDAY_RULES[country as CountryId]

  if (!rules) {
    return new Set()
  }

  const key = `${country}-${year}`
  if (!holidaysCache.has(key)) {
    holidaysCache.set(key, buildHolidays(year, rules))
  }

  return holidaysCache.get(key)
}

const isWorkingDay = (date: DateTime, country?: string): boolean =>
  date.weekday <= WORKING_DAYS_IN_WEEK &&
  !getHolidays(date.year, country).has(date.toISODate())

export const addWorkingDays = (
  from: DateTime,
  days: number,
  country?: string
): DateTime => {
  let result = from
  let remaining = days

  while (remaining > 0) {
    result = result.plus({ days: 1 })
    if (isWorkingDay(result, country)) {
      remaining -= 1
    }
  }

  while (!isWorkingDay(result, country)) {
    result = result.plus({ days: 1 })
  }

  return result
}
